/**
 * Horizontal Source List Component
 * Displays sources in a horizontally scrollable list with navigation arrows
 */

import '../../../../styles/oc-utilities.css';

import React, { AriaAttributes, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { animated, useSpring } from 'react-spring';

import { SPRING_CONFIG_DEFAULT } from '../../../../constants';
import { MessageReference } from '../../../Message/types/messages';
import { useElementBox } from '../../hooks/useElementBox';
import {
  ArrowIcon,
  LeftArrowButton,
  RightArrowButton,
  ScrollContainer,
  SourceGrid,
  SourceItem,
} from './HorizontalSourceList.styled';
import SourceCard from './SourceCard';

export type HorizontalSourceListProps = {
  messageId: string;
  sources: MessageReference[];
  isExpanded?: boolean;
  id?: string;
  role?: string;
} & AriaAttributes;

const SOURCE_WIDTH = 285;

/**
 * HorizontalSourceList Component
 * Displays a horizontally scrollable list of source cards with navigation arrows
 */
const HorizontalSourceList: React.FC<HorizontalSourceListProps> = ({
  messageId,
  sources,
  isExpanded = true,
  ...ariaProps
}) => {
  const [innerRef, { height }] = useElementBox();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);

  const styles = useSpring({
    height: `${isExpanded ? height + 8 : 0}px`,
    overflow: isExpanded ? 'unset' : 'hidden',
    onChange: () => {
      if (isExpanded && bottomRef.current) {
        bottomRef.current.scrollIntoView({ block: 'nearest' });
      }
    },
    config: SPRING_CONFIG_DEFAULT,
  });

  const [[showLeft, showRight], setShowValues] = useState([false, false]);

  const updateScroll = useCallback(() => {
    if (!scrollerEl) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollerEl;

    const canScroll = scrollWidth - clientWidth > 1;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    setShowValues([canScroll && !atStart, canScroll && !atEnd]);
  }, [scrollerEl]);

  useLayoutEffect(() => {
    if (!scrollerEl) return;

    const contentEl = scrollerEl.firstElementChild as HTMLElement | null;

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateScroll);
    });

    ro.observe(scrollerEl);
    if (contentEl) ro.observe(contentEl);

    requestAnimationFrame(updateScroll);

    return () => ro.disconnect();
  }, [scrollerEl, updateScroll, sources.length, isExpanded]);

  
  return (
    isExpanded && (
      <animated.div
        style={{
          ...styles,
          width: '100%',
          maxWidth: '100%',
          position: 'relative',
        }}
        {...ariaProps}
      >
        {showLeft && (
          <LeftArrowButton
            type="button"
            onClick={() => {
              scrollerEl?.scrollTo({
                left: (scrollerEl?.scrollLeft ?? 0) - SOURCE_WIDTH,
                behavior: 'smooth',
              });
            }}
            aria-hidden="true"
            tabIndex={-1}
          >
            <ArrowIcon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </ArrowIcon>
          </LeftArrowButton>
        )}

        <ScrollContainer ref={setScrollerEl} onScroll={updateScroll} $atStart={!showLeft}>
          <div ref={innerRef}>
            <SourceGrid $atStart={!showLeft}>
              {sources.map((source, index) => (
                <SourceItem key={`${messageId}-source-${index}`}>
                  <SourceCard source={source} ordinal={index + 1} />
                </SourceItem>
              ))}
            </SourceGrid>
          </div>
        </ScrollContainer>

        {showRight && (
          <RightArrowButton
            type="button"
            onClick={() => {
              scrollerEl?.scrollTo({
                  left: (scrollerEl?.scrollLeft ?? 0) + SOURCE_WIDTH,
                  behavior: 'smooth',})
            }}
            aria-hidden="true"
            tabIndex={-1}
          >
            <ArrowIcon
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </ArrowIcon>
          </RightArrowButton>
        )}

        <div
          ref={bottomRef}
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0
          }}
        />
      </animated.div>
    )
  ) || null;
};

export default React.memo(HorizontalSourceList);